import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useMobileContext } from '../components/Layout';
import { MobileHeader } from '../components/mobile';
import { Building2, Users, Home, MapPin, Phone, Mail, Globe, Save, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MonCabinet = () => {
  const { user, token } = useAuth();
  const { isMobile, onMenuClick } = useMobileContext();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cabinet, setCabinet] = useState(null);
  const [stats, setStats] = useState({ buildings: 0, users: 0 });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [formData, setFormData] = useState({
    nom: '',
    adresse: '',
    ville: '',
    code_postal: '',
    telephone: '',
    email: '',
    site_web: ''
  });

  useEffect(() => {
    if (token && user?.cabinet_id) {
      fetchCabinet();
    } else {
      setLoading(false);
    }
  }, [token, user?.cabinet_id]);

  const fetchCabinet = async () => {
    try {
      const response = await axios.get(`${API}/cabinets/${user.cabinet_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCabinet(response.data);
      setFormData({
        nom: response.data.nom || '',
        adresse: response.data.adresse || '',
        ville: response.data.ville || '',
        code_postal: response.data.code_postal || '',
        telephone: response.data.telephone || '',
        email: response.data.email || '',
        site_web: response.data.site_web || ''
      });
      
      // Fetch stats
      const buildingsRes = await axios.get(`${API}/buildings?cabinet_id=${user.cabinet_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setStats({
        buildings: buildingsRes.data?.length || 0,
        users: response.data.users_count || 0
      });
    } catch (error) {
      console.error('Error fetching cabinet:', error);
      setMessage({ type: 'error', text: 'Erreur lors du chargement des informations du cabinet' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await axios.put(`${API}/cabinets/${user.cabinet_id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: 'Informations du cabinet mises à jour' });
    } catch (error) {
      console.error('Error updating cabinet:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erreur lors de la mise à jour' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        {isMobile && (
          <MobileHeader 
            title="Mon Cabinet"
            onMenuClick={onMenuClick}
          />
        )}
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00D3A8]"></div>
        </div>
      </>
    );
  }

  if (!user?.cabinet_id) {
    return (
      <>
        {isMobile && (
          <MobileHeader 
            title="Mon Cabinet"
            onMenuClick={onMenuClick}
          />
        )}
        <div className={`max-w-4xl mx-auto ${isMobile ? 'p-4' : ''}`}>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
            <AlertCircle className="mx-auto mb-4 text-amber-500" size={48} />
            <h2 className="text-xl font-semibold text-amber-800 mb-2">Aucun cabinet associé</h2>
            <p className="text-amber-700">
              Votre compte n'est pas encore associé à un cabinet. 
              Veuillez contacter l'administrateur pour être rattaché à un cabinet.
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {isMobile && (
        <MobileHeader 
          title="Mon Cabinet"
          onMenuClick={onMenuClick}
        />
      )}
      <div className={`max-w-4xl mx-auto space-y-6 ${isMobile ? 'p-4' : ''}`}>
        {!isMobile && <h1 className="text-2xl font-bold text-gray-800">Mon Cabinet</h1>}

        {/* Message */}
        {message.text && (
          <div className={`p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' :
            'bg-red-50 text-red-700 border border-red-200'
          }`}>
            {message.text}
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Home className="text-blue-600" size={24} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800">{stats.buildings}</p>
                <p className="text-sm text-gray-500">Immeubles gérés</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <Users className="text-green-600" size={24} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800">{stats.users}</p>
                <p className="text-sm text-gray-500">Collaborateurs</p>
              </div>
            </div>
          </div>
        </div>

        {/* Cabinet Info Form */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-[#00D3A8] to-[#00C7A1] px-6 py-4">
            <div className="flex items-center space-x-3 text-white">
              <Building2 size={24} />
              <h2 className="text-xl font-semibold">{formData.nom || 'Mon Cabinet'}</h2>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Nom du cabinet */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Building2 size={16} className="inline mr-2" />
                  Nom du cabinet
                </label>
                <input
                  type="text"
                  name="nom"
                  value={formData.nom}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                  data-testid="input-cabinet-nom"
                />
              </div>

              {/* Adresse */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin size={16} className="inline mr-2" />
                  Adresse
                </label>
                <input
                  type="text"
                  name="adresse"
                  value={formData.adresse}
                  onChange={handleInputChange}
                  placeholder="123 Rue de la Gestion"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                  data-testid="input-cabinet-adresse"
                />
              </div>

              {/* Ville */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ville
                </label>
                <input
                  type="text"
                  name="ville"
                  value={formData.ville}
                  onChange={handleInputChange}
                  placeholder="Paris"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                  data-testid="input-cabinet-ville"
                />
              </div>

              {/* Code postal */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Code postal
                </label>
                <input
                  type="text"
                  name="code_postal"
                  value={formData.code_postal}
                  onChange={handleInputChange}
                  placeholder="75000"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                  data-testid="input-cabinet-cp"
                />
              </div>

              {/* Téléphone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Phone size={16} className="inline mr-2" />
                  Téléphone
                </label>
                <input
                  type="tel"
                  name="telephone"
                  value={formData.telephone}
                  onChange={handleInputChange}
                  placeholder="01 23 45 67 89"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                  data-testid="input-cabinet-tel"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Mail size={16} className="inline mr-2" />
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="contact@cabinet.fr"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                  data-testid="input-cabinet-email"
                />
              </div>

              {/* Site web */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Globe size={16} className="inline mr-2" />
                  Site web
                </label>
                <input
                  type="url"
                  name="site_web"
                  value={formData.site_web}
                  onChange={handleInputChange}
                  placeholder="https://www.moncabinet.fr"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                  data-testid="input-cabinet-web"
                />
              </div>
            </div>

            {/* Submit */}
            <div className="pt-4 border-t border-gray-200">
              <button
                type="submit"
                disabled={saving}
                className="bg-[#00D3A8] hover:bg-[#00C7A1] text-white py-3 px-6 rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50"
                data-testid="save-cabinet-btn"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Enregistrement...</span>
                  </>
                ) : (
                  <>
                    <Save size={20} />
                    <span>Enregistrer les modifications</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default MonCabinet;
